import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type { ConvoSession, ConvoSessionWithNotes, ConvoSessionSearchParams } from '../types/convo.js';
import * as convoApi from './convo-api.js';
import { wsClient } from './websocket-client.js';

export type ConvoLoadingState = 'idle' | 'loading' | 'loaded' | 'error';

let sharedSessions: ConvoSession[] = [];
let sharedActiveSession: ConvoSessionWithNotes | null = null;
let sharedLoadingState: ConvoLoadingState = 'idle';
let sharedError: string | null = null;
let sharedSearchParams: ConvoSessionSearchParams = {};

const hosts = new Set<ReactiveControllerHost>();

function notifyHosts(): void {
  for (const host of hosts) {
    host.requestUpdate();
  }
}

async function loadSessions(params: ConvoSessionSearchParams = {}): Promise<void> {
  if (sharedLoadingState === 'loading') return;
  sharedLoadingState = 'loading';
  sharedError = null;
  sharedSearchParams = params;
  notifyHosts();

  try {
    sharedSessions = await convoApi.getSessions(params);
    sharedLoadingState = 'loaded';
  } catch (e) {
    sharedLoadingState = 'error';
    sharedError = e instanceof Error ? e.message : 'Sessions konnten nicht geladen werden';
  }

  notifyHosts();
}

function onWsMessage(e: Event): void {
  const detail = (e as CustomEvent).detail as { type: string };
  const type = detail.type;

  if (type === 'convo:session-created' || type === 'convo:session-updated' || type === 'convo:session-deleted') {
    void loadSessions(sharedSearchParams);
  }
}

wsClient.addEventListener('ws-message', onWsMessage);

export class ConvoStateController implements ReactiveController {
  private host: ReactiveControllerHost;

  constructor(host: ReactiveControllerHost) {
    this.host = host;
    host.addController(this);
  }

  hostConnected(): void {
    hosts.add(this.host);
  }

  hostDisconnected(): void {
    hosts.delete(this.host);
  }

  get sessions(): ConvoSession[] {
    return sharedSessions;
  }

  get activeSession(): ConvoSessionWithNotes | null {
    return sharedActiveSession;
  }

  get loadingState(): ConvoLoadingState {
    return sharedLoadingState;
  }

  get error(): string | null {
    return sharedError;
  }

  async loadAll(params?: ConvoSessionSearchParams): Promise<void> {
    await loadSessions(params);
  }

  async loadOne(id: number): Promise<void> {
    try {
      sharedActiveSession = await convoApi.getSession(id);
      notifyHosts();
    } catch (e) {
      sharedError = e instanceof Error ? e.message : 'Session konnte nicht geladen werden';
      notifyHosts();
    }
  }

  async refresh(): Promise<void> {
    await loadSessions(sharedSearchParams);
  }
}
